import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TrendingUp, DollarSign, Calendar, PieChart } from 'lucide-react';
import axios from 'axios';
import { format, subDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RevenueAnalytics = () => {
  const { selectedProperty } = useOutletContext();
  const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedProperty) {
      fetchAnalytics();
    }
  }, [selectedProperty]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${API}/rate-plans/analytics/${selectedProperty.id}?start_date=${startDate}&end_date=${endDate}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchAnalytics();
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="revenue-analytics-page">
      <div>
        <h1 className="text-3xl font-bold text-slate-900">Revenue Analytics</h1>
        <p className="text-slate-600 mt-1">Track revenue performance and key metrics</p>
      </div>

      {/* Date Range Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div>
              <label className="text-sm text-slate-600">Start Date</label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-1"
                data-testid="start-date-input"
              />
            </div>
            <div>
              <label className="text-sm text-slate-600">End Date</label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-1"
                data-testid="end-date-input"
              />
            </div>
            <Button onClick={handleSearch} className="mt-6" data-testid="search-button">
              Update
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-12">Loading analytics...</div>
      ) : analytics ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="border-l-4 border-l-cyan-500" data-testid="revenue-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <DollarSign size={18} />
                  Total Revenue
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">
                  ${analytics.total_revenue.toLocaleString()}
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  {analytics.total_reservations} reservation(s)
                </p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-blue-500" data-testid="adr-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <TrendingUp size={18} />
                  ADR (Avg Daily Rate)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">
                  ${analytics.adr}
                </div>
                <p className="text-sm text-slate-500 mt-1">Per occupied room</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-emerald-500" data-testid="revpar-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <PieChart size={18} />
                  RevPAR
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">
                  ${analytics.revpar}
                </div>
                <p className="text-sm text-slate-500 mt-1">Revenue per available room</p>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-amber-500" data-testid="occupancy-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <Calendar size={18} />
                  Occupancy Rate
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-slate-900">
                  {analytics.occupancy_rate}%
                </div>
                <p className="text-sm text-slate-500 mt-1">
                  {analytics.total_nights} room night(s)
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Revenue by Source */}
          <Card data-testid="revenue-by-source">
            <CardHeader>
              <CardTitle>Revenue by Source</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(analytics.revenue_by_source).length === 0 ? (
                <p className="text-slate-500 text-center py-8">No revenue data for this period</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(analytics.revenue_by_source).map(([source, data]) => (
                    <div key={source} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-900">{source}</p>
                        <p className="text-sm text-slate-600">{data.count} reservation(s)</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xl font-bold text-cyan-600">
                          ${data.revenue.toLocaleString()}
                        </p>
                        <p className="text-sm text-slate-500">
                          {((data.revenue / analytics.total_revenue) * 100).toFixed(1)}%
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <TrendingUp size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Select a date range to view analytics</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default RevenueAnalytics;
